/**
 * File: invoiceBooksService.ts
 * Path: apps/admin/src/shared/services/invoiceBooksService.ts
 * Description: The invoice's trips to the server after it is issued (the
 * invoiceBooks callable: issue or discard a draft, void, credit notes, use a
 * client's credit, put an older invoice in the books, the PDFs again) and
 * the credit notes, read straight from creditNotes/<id> (the server is the
 * only writer).
 */
import { collection, getDocs, limit, query } from "firebase/firestore";
import {
  CREDIT_NOTE_COLLECTIONS,
  db,
  FunctionsService,
  toCreditNote,
  type Allocation,
  type CreditNote,
} from "@idk/config";

export interface InvoiceBooksResult {
  filename?: string;
  base64?: string;
  number?: string;
  journalNumber?: number | null;
  message: string;
}

export type InvoiceBooksAction =
  | { action: "issueDraft"; invoiceId: string }
  | { action: "discardDraft"; invoiceId: string; reason: string }
  | { action: "voidInvoice"; invoiceId: string; reason: string }
  | { action: "creditNote"; invoiceNumber: string; cents: number; reason: string; issuedOn?: string }
  | { action: "applyCredit"; clientId: string | null; party: string; date?: string; allocations: Allocation[] }
  | { action: "bookLegacy"; invoiceId: string }
  | { action: "invoicePdf"; invoiceId: string }
  | { action: "creditNotePdf"; creditNoteId: string };

export const invoiceBooks = (input: InvoiceBooksAction) => FunctionsService.call<InvoiceBooksAction, InvoiceBooksResult>("invoiceBooks", input);

export const CREDIT_NOTES_LIST_LIMIT = 500;

export const listCreditNotes = async (): Promise<CreditNote[]> => {
  const snap = await getDocs(query(collection(db, CREDIT_NOTE_COLLECTIONS.creditNotes), limit(CREDIT_NOTES_LIST_LIMIT)));
  return snap.docs.flatMap((d) => toCreditNote(d.id, d.data()) ?? []).sort((a, b) => b.number.localeCompare(a.number));
};
